import type { WorkflowEdge, WorkflowNode, WorkflowTrigger } from "@/types/aiToolkit";

export type WorkflowRunStatus =
  | "pending"
  | "running"
  | "paused"
  | "waiting_approval"
  | "waiting_chat"
  | "completed"
  | "failed"
  | "cancelled";

export type WorkflowEventType =
  | "started"
  | "completed"
  | "failed"
  | "skipped"
  | "edited"
  | "branched"
  | "paused"
  | "resumed"
  | "chat_forked"
  | "chat_merged"
  | "artifact_created"
  | "parallel_fork"
  | "parallel_join";

export type CheckpointType = "pause" | "approval" | "chat_fork";

export type ArtifactType =
  | "thread"
  | "kb_document"
  | "rag_index"
  | "clickup_task"
  | "gmail_draft"
  | "email_label"
  | "generic";

export interface ValidationResult {
  valid: boolean;
  errors?: string[];
}

export interface EntityRegistry {
  people: Array<{ name: string; email?: string; clickupUserId?: string }>;
  dates: Array<{ raw: string; iso?: string }>;
}

export interface ContextBlock {
  source: string;
  title: string;
  content: string;
  metadata?: Record<string, unknown>;
}

export interface NodeOutput {
  raw: string;
  parsed?: unknown;
  confidence?: number;
  quality?: number;
  validation?: ValidationResult;
  toolRequest?: { toolId: string; operation: string; input: unknown };
  artifacts?: string[];
  model?: string;
  editedByUser?: boolean;
}

export interface WorkflowRunState {
  variables: Record<string, unknown>;
  contextBlocks: Record<string, ContextBlock>;
  nodeOutputs: Record<string, NodeOutput>;
  /** 4A.5: per-node inputs resolved from incoming-edge dataMapping (opt-in). */
  nodeInputs?: Record<string, Record<string, unknown>>;
  entities: EntityRegistry;
  parallel: {
    activeBranches: Record<string, { nodeIds: string[]; status: string }>;
    mergeBuffers: Record<string, NodeOutput[]>;
  };
  metadata: {
    source?: string;
    itemId?: string;
    approvedHumanReview?: Record<string, boolean>;
    approvedTools?: Record<string, boolean>;
    pendingTool?: {
      nodeId: string;
      toolId: string;
      operation: string;
      input: Record<string, unknown>;
    };
    dismissed?: boolean;
    /** 4A.4: per-loop-node iteration counters (nodePure.decideLoop) */
    loopCounters?: Record<string, number>;
  };
}

export function createEmptyRunState(triggerPayload?: Record<string, unknown>): WorkflowRunState {
  return {
    variables: { ...(triggerPayload ?? {}) },
    contextBlocks: {},
    nodeOutputs: {},
    entities: { people: [], dates: [] },
    parallel: { activeBranches: {}, mergeBuffers: {} },
    metadata: {},
  };
}

export interface WorkflowVersion {
  id: string;
  workflowId: string;
  userId: string;
  versionNumber: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  entryNodeId: string | null;
  trigger: WorkflowTrigger;
  variables?: unknown[];
  changelog?: string | null;
  publishedAt: string;
}

export interface WorkflowRun {
  id: string;
  userId: string;
  workflowId: string;
  workflowVersionId: string | null;
  status: WorkflowRunStatus;
  triggerType: string | null;
  triggerPayload: Record<string, unknown>;
  currentNodeId: string | null;
  state: WorkflowRunState;
  policySnapshot: Record<string, unknown>;
  error: string | null;
  startedAt: string | null;
  completedAt: string | null;
  createdAt: string;
}

export interface WorkflowRunEvent {
  id: string;
  runId: string;
  nodeId: string | null;
  nodeType: string | null;
  eventType: WorkflowEventType;
  inputSnapshot?: unknown;
  outputSnapshot?: unknown;
  confidence?: number | null;
  quality?: number | null;
  validationResult?: ValidationResult | null;
  durationMs?: number | null;
  actor: "system" | "user";
  parallelBranchId?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface WorkflowCheckpoint {
  id: string;
  runId: string;
  nodeId: string;
  checkpointType: CheckpointType;
  outputOverride?: unknown;
  chatThreadId?: string | null;
  resolvedAt?: string | null;
  createdAt: string;
}

export interface WorkflowArtifact {
  id: string;
  runId: string;
  nodeId: string | null;
  userId: string;
  artifactType: ArtifactType;
  threadId?: string | null;
  kbDocumentId?: string | null;
  clickupTaskId?: string | null;
  gmailId?: string | null;
  externalId?: string | null;
  title?: string | null;
  url?: string | null;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export type RagStorageTarget =
  | { type: "kb"; documentId?: string }
  | { type: "integration"; source: string; externalId: string }
  | { type: "thread"; threadId?: string };

export interface OrganizationPolicy {
  id: string;
  userId: string | null;
  workspaceId: string | null;
  scope: "organization" | "workspace" | "workflow" | "agent";
  allowedModels: string[];
  security: Record<string, unknown>;
  piiRules: Record<string, unknown>;
  retention: Record<string, unknown>;
  costLimits: Record<string, unknown>;
  approvalThreshold: Record<string, unknown>;
}

export interface WorkerProfile {
  id: string;
  userId: string | null;
  name: string;
  description?: string | null;
  preferredModel?: string | null;
  fallbackModels: string[];
  temperature: number;
  maxTokens?: number | null;
  reasoningMode: boolean;
  creativity: string;
  determinism: string;
  speedTier: string;
  budgetPolicy: Record<string, unknown>;
  contextWindowHint?: string | null;
  isBuiltin: boolean;
}

export interface UserAgentSkill {
  id: string;
  userId: string;
  name: string;
  description?: string | null;
  instructions: string;
  integrationScopes: string[];
  outputSchema?: unknown;
  isTemplate: boolean;
  updatedAt?: string | null;
}
